import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, X, CheckCircle2, Play, Star, UserRound } from 'lucide-react';
import { neonQuery } from '../services/api';
import { BRANDS } from '../lib/brands';

/**
 * Modal picker for the Media Library (media_assets table).
 *
 * Props:
 *   - multiSelect (bool): if true, "Done" returns an array of assets; otherwise
 *     clicking a tile returns immediately.
 *   - onClose: required. Called on backdrop click or X button.
 *   - onPick: required. Receives the picked asset(s).
 *   - initialSelection (array of asset ids): pre-select these in multi mode.
 *
 * Filters mirror the Media Library page (brand / category / type / orientation /
 * quality / starred / sort) and results PAGE with a "Show more" button — added
 * 2026-08-18 after Rebecca hit the wall in Content Builder: one screen of
 * results, no way to page, none of the library's filters.
 */
// Keys whose extension means "this is a video, not an image" — used to filter
// out video assets when the caller asks for imagesOnly (e.g. picking a recipe
// hero photo where a .mov wouldn't display).
const VIDEO_EXT_RE = /\.(mov|mp4|m4v|webm|avi|mkv)$/i;
const isVideoAsset = (a) => a?.media_kind === 'video' || VIDEO_EXT_RE.test(String(a?.r2_original_key || ''));
const fmtDur = (s) => {
  const n = Math.round(Number(s) || 0);
  if (!n) return null;
  const m = Math.floor(n / 60);
  return `${m}:${String(n % 60).padStart(2, '0')}`;
};

const PAGE = 60;

export default function MediaPicker({ multiSelect = false, onClose, onPick, initialSelection = [], imagesOnly = false, videosOnly = false, audioOnly = false }) {
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(null);
  const [brand, setBrand] = useState(null);
  const [orientation, setOrientation] = useState(null);
  const [starredOnly, setStarredOnly] = useState(false);
  // null = all types. 'photo' | 'video' | 'gif' — same values the Media Library
  // page's Type chips pass to media_assets_list. Hidden when the caller already
  // locks the type via imagesOnly / videosOnly.
  const [kind, setKind] = useState(null);
  const [minQuality, setMinQuality] = useState(3);
  // null = anyone, 'unknown' = legacy rows with no uploader recorded, otherwise
  // an admin_id. Jessica, 2026-09-14: she is mainly posting the photos and videos
  // SHE uploaded, and had no way to narrow to them from inside a builder — the
  // Media Library page had this filter, every picker that opens on top of another
  // page did not. It lives HERE rather than in one builder precisely because she
  // asked for it to be uniform: this component is the picker for Content Builder,
  // the Social/Meta/Instagram/TikTok builders, Marketing Studio, Video Studio,
  // Recipes and the Site Content tabs, so one copy covers all of them.
  const [uploader, setUploader] = useState(null);
  const [uploaders, setUploaders] = useState([]);   // [{admin_id, name, count}]
  // Default to newest — Greg's most common need is "the photos I just added".
  // Backend whitelists: newest | oldest | quality | name | uploader (neon media_assets_list).
  const [orderBy, setOrderBy] = useState('newest');
  const [rows, setRows] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [err, setErr] = useState(null);
  const [picked, setPicked] = useState(() => new Set(initialSelection));
  const [rowsById, setRowsById] = useState(new Map());
  // Monotonic request id so a slow earlier query can never overwrite a newer
  // one (the documented search-race gotcha from the UPS Bills reconcile page).
  const reqRef = useRef(0);

  const runQuery = useCallback((offset) => neonQuery('media_assets_list', {
    search, category, brand, orientation, starredOnly, minQuality, orderBy,
    uploadedByAdminId: uploader,
    mediaKind: videosOnly ? 'video' : (audioOnly ? 'audio' : kind),
    usableForAny: [], tagsAll: [], excludeTags: [],  // HEIC uploads are stored as JPEG - 'source-heic' is provenance, not a format problem
    includeArchived: false, limit: PAGE, offset,
  }), [search, category, brand, orientation, starredOnly, kind, minQuality, orderBy, uploader, videosOnly, audioOnly]);

  // Who has uploaded anything, for the "Uploaded by" dropdown. Fetched once —
  // the list changes about as often as staff do. A failure is non-fatal: the
  // dropdown just stays on "Anyone" rather than taking the picker down with it.
  useEffect(() => {
    let live = true;
    neonQuery('media_assets_uploader_counts')
      .then((r) => { if (live) setUploaders(Array.isArray(r) ? r : []); })
      .catch(() => { /* filter is an enhancement, never a dependency */ });
    return () => { live = false; };
  }, []);

  const mergeById = (list) => {
    // Keep a map so the "Done" button in multiSelect mode can resolve picked ids
    // to full asset objects even if the user changed the filters after selecting.
    setRowsById((prev) => {
      const next = new Map(prev);
      for (const a of (list || [])) next.set(a.id, a);
      return next;
    });
  };

  useEffect(() => {
    const id = ++reqRef.current;
    setLoading(true); setErr(null);
    runQuery(0)
      .then((r) => {
        if (reqRef.current !== id) return;
        setRows(r || []);
        setHasMore((r || []).length === PAGE);
        mergeById(r);
      })
      .catch((e) => { if (reqRef.current === id) setErr(e.message); })
      .finally(() => { if (reqRef.current === id) setLoading(false); });
  }, [runQuery]);

  const loadMore = async () => {
    setLoadingMore(true); setErr(null);
    try {
      const r = await runQuery(rows.length);
      setRows((prev) => [...prev, ...(r || [])]);
      setHasMore((r || []).length === PAGE);
      mergeById(r);
    } catch (e) { setErr(e.message); }
    setLoadingMore(false);
  };

  const togglePick = (asset) => {
    if (!multiSelect) { onPick(asset); return; }
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(asset.id)) next.delete(asset.id);
      else next.add(asset.id);
      return next;
    });
  };

  const finishMulti = () => {
    const assets = Array.from(picked).map((id) => rowsById.get(id)).filter(Boolean);
    onPick(assets);
  };

  const shown = imagesOnly ? rows.filter((r) => !isVideoAsset(r)) : rows;

  return (
    <div onClick={onClose} style={backdropStyle}>
      <div onClick={(e) => e.stopPropagation()} style={modalStyle}>
        <div style={headerStyle}>
          <h3 style={{ margin: 0, fontSize: 16, color: '#00395C', whiteSpace: 'nowrap' }}>Pick from Media Library</h3>
          <div style={{ flex: 1, position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#6e6e73' }} />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={videosOnly ? 'Search videos…' : audioOnly ? 'Search narration and music…' : 'Search by name, tag, subject…'} style={searchInputStyle} autoFocus />
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer' }}>
            <X size={18} color="#6e6e73" />
          </button>
        </div>
        <div style={filterRowStyle}>
          <select value={brand || ''} onChange={(e) => setBrand(e.target.value || null)} style={selectStyle} title="Brand">
            <option value="">All brands</option>
            {BRANDS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
          <select value={category || ''} onChange={(e) => setCategory(e.target.value || null)} style={selectStyle}>
            <option value="">All categories</option>
            <option value="products">products</option>
            <option value="plated">plated</option>
            <option value="lifestyle">lifestyle</option>
            <option value="ugc">ugc</option>
            <option value="process">process</option>
            <option value="people">people</option>
            <option value="marketing">marketing</option>
          </select>
          {!videosOnly && !audioOnly ? (
            <select value={kind || ''} onChange={(e) => setKind(e.target.value || null)} style={selectStyle} title="Media type">
              <option value="">All types</option>
              <option value="photo">Images</option>
              {!imagesOnly ? <option value="video">Videos</option> : null}
              <option value="gif">GIFs</option>
              {!imagesOnly ? <option value="audio">Audio</option> : null}
            </select>
          ) : null}
          <select value={orientation || ''} onChange={(e) => setOrientation(e.target.value || null)} style={selectStyle} title="Orientation">
            <option value="">Any shape</option>
            <option value="landscape">Landscape</option>
            <option value="portrait">Portrait</option>
            <option value="square">Square</option>
          </select>
          <select value={minQuality} onChange={(e) => setMinQuality(parseInt(e.target.value, 10))} style={selectStyle}>
            <option value="0">any quality</option>
            <option value="3">3+ stars</option>
            <option value="4">4+ stars</option>
            <option value="5">5 stars only</option>
          </select>
          <select value={uploader == null ? '' : String(uploader)}
            onChange={(e) => {
              const v = e.target.value;
              setUploader(v === '' ? null : (v === 'unknown' ? 'unknown' : parseInt(v, 10)));
            }}
            style={selectStyle} title="Who uploaded it">
            <option value="">Anyone's uploads</option>
            {uploaders.map((u) => (
              <option key={u.admin_id == null ? 'unknown' : u.admin_id}
                value={u.admin_id == null ? 'unknown' : u.admin_id}>
                {u.admin_id == null ? 'No uploader recorded' : u.name} ({u.count})
              </option>
            ))}
          </select>
          <select value={orderBy} onChange={(e) => setOrderBy(e.target.value)} style={selectStyle} title="Sort order">
            <option value="newest">Newest</option>
            <option value="oldest">Oldest</option>
            <option value="quality">Best quality</option>
            <option value="name">Name (A–Z)</option>
            <option value="uploader">Uploaded by</option>
          </select>
          <button onClick={() => setStarredOnly(!starredOnly)} title="Only Greg's starred picks"
            style={{ ...starBtnStyle, ...(starredOnly ? starBtnOnStyle : null) }}>
            <Star size={13} fill={starredOnly ? '#DC582A' : 'none'} /> Starred
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#f5f5f7' }}>
          {err ? <div style={{ color: '#c33', padding: 10 }}>{err}</div> : null}
          {loading && rows.length === 0 ? <div style={{ padding: 20, color: '#6e6e73' }}>Loading…</div> : null}
          {!loading && !shown.length ? (
            <div style={{ padding: 20, color: '#6e6e73' }}>
              Nothing matches. Try fewer words, drop a filter, or set quality to "any" — some good photos are rated under 3 stars.
            </div>
          ) : null}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
            {shown.map((r) => {
              const isPicked = picked.has(r.id);
              const video = isVideoAsset(r);
              return (
                <button key={r.id} onClick={() => togglePick(r)}
                  style={{ ...tileStyle, outline: isPicked ? '3px solid #DC582A' : 'none' }}>
                  <div style={tileThumbStyle}>
                    <img src={`/.netlify/functions/r2-image?key=${encodeURIComponent(r.r2_thumb_key)}`}
                      alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    <div style={qualityBadgeStyle}>{'★'.repeat(r.quality_score || 0)}</div>
                    {video ? (
                      <div style={videoBadgeStyle}><Play size={10} fill="#fff" stroke="#fff" />{fmtDur(r.duration_s) || 'Video'}</div>
                    ) : null}
                    {isPicked ? (
                      <div style={pickedBadgeStyle}><CheckCircle2 size={14} /></div>
                    ) : null}
                  </div>
                  <div style={tileMetaStyle}>
                    <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.suggested_name}</div>
                    <div style={{ color: '#6e6e73', fontSize: 10 }}>{r.category}/{r.subcategory}</div>
                    {/* Shown so sorting by uploader reads as grouped rather than
                        shuffled, and so you can tell whose photo you picked. */}
                    {r.uploaded_by_name ? (
                      <div style={uploaderLineStyle} title={`Uploaded by ${r.uploaded_by_name}`}>
                        <UserRound size={9} style={{ flexShrink: 0 }} /> {r.uploaded_by_name}
                      </div>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
          {(hasMore || loadingMore) ? (
            <div style={{ textAlign: 'center', padding: '16px 0 4px' }}>
              <button onClick={loadMore} disabled={loadingMore} style={moreBtnStyle}>
                {loadingMore ? 'Loading…' : `Show more (${rows.length} loaded)`}
              </button>
            </div>
          ) : (rows.length > PAGE - 1 ? (
            <div style={{ textAlign: 'center', padding: '14px 0 2px', color: '#6e6e73', fontSize: 12 }}>
              That is everything that matches ({shown.length}).
            </div>
          ) : null)}
        </div>
        {multiSelect ? (
          <div style={footerStyle}>
            <div style={{ fontSize: 12, color: '#6e6e73' }}>
              {picked.size} selected
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={onClose} style={cancelBtnStyle}>Cancel</button>
              <button onClick={finishMulti} disabled={picked.size === 0}
                style={{ ...doneBtnStyle, opacity: picked.size === 0 ? 0.5 : 1, cursor: picked.size === 0 ? 'not-allowed' : 'pointer' }}>
                Use {picked.size} item{picked.size === 1 ? '' : 's'}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const backdropStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
};

const modalStyle = {
  background: '#fff', borderRadius: 10, width: '100%', maxWidth: 1100, maxHeight: '90vh',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
};

const headerStyle = {
  padding: '14px 20px 10px', display: 'flex', alignItems: 'center', gap: 12,
};

const filterRowStyle = {
  padding: '0 20px 12px', borderBottom: '1px solid #e5e5ea',
  display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
};

const footerStyle = {
  padding: '12px 20px', borderTop: '1px solid #e5e5ea', background: '#fafafa',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
};

const searchInputStyle = {
  width: '100%', padding: '7px 32px', border: '1px solid #e5e5ea',
  borderRadius: 6, fontSize: 13, boxSizing: 'border-box',
};

const selectStyle = {
  padding: '7px 10px', fontSize: 13, border: '1px solid #e5e5ea', borderRadius: 4,
  background: '#fff', fontFamily: 'inherit', color: '#1d1d1f', boxSizing: 'border-box',
};

const starBtnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 10px', fontSize: 13,
  border: '1px solid #e5e5ea', borderRadius: 4, background: '#fff', color: '#1d1d1f',
  cursor: 'pointer', fontFamily: 'inherit',
};

const starBtnOnStyle = { borderColor: '#DC582A', color: '#DC582A', background: '#fff6f1' };

const moreBtnStyle = {
  background: '#00395C', color: '#fff', border: 'none', borderRadius: 6,
  padding: '9px 22px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
};

const tileStyle = {
  background: '#fff', border: '1px solid #e5e5ea', borderRadius: 6,
  padding: 0, cursor: 'pointer', overflow: 'hidden',
  display: 'flex', flexDirection: 'column',
};

const tileThumbStyle = {
  aspectRatio: '1 / 1', background: '#f0f0f3', overflow: 'hidden', position: 'relative',
};

const uploaderLineStyle = {
  marginTop: 2, fontSize: 9.5, color: '#6e6e73',
  display: 'flex', alignItems: 'center', gap: 3,
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
};

const tileMetaStyle = {
  padding: 6, fontSize: 11, color: '#00395C', textAlign: 'left',
};

const qualityBadgeStyle = {
  position: 'absolute', top: 4, left: 4, background: 'rgba(0,0,0,0.6)',
  color: '#ffd60a', fontSize: 10, padding: '1px 5px', borderRadius: 3, fontFamily: 'monospace',
};

const videoBadgeStyle = {
  position: 'absolute', bottom: 4, left: 4, background: 'rgba(0,0,0,0.72)',
  color: '#fff', fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 3,
  display: 'inline-flex', alignItems: 'center', gap: 3,
};

const pickedBadgeStyle = {
  position: 'absolute', top: 4, right: 4, background: '#DC582A',
  color: '#fff', borderRadius: '50%', width: 22, height: 22,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};

const doneBtnStyle = {
  background: '#DC582A', color: '#fff', border: 'none', borderRadius: 6,
  padding: '8px 16px', fontSize: 13, fontWeight: 600,
};

const cancelBtnStyle = {
  background: '#fff', color: '#6e6e73', border: '1px solid #e5e5ea',
  borderRadius: 6, padding: '8px 14px', fontSize: 13, cursor: 'pointer',
};
